import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { type RichText } from '@atlaskit/linking-types';
import { logError } from '@databases/logs';
// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux';
import { type ObjectMode, type Optional } from '@databases/types';
import {
  type JiraIssueRef,
  type ResolvedJiraIssueRef,
} from '@databases/types/confluence';
import {
  type DatabaseField,
  DatabaseFieldType,
  type EntryWithContext,
  type FieldWithContext,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { isEqual } from 'lodash-es';

import { parseStorageFormat } from '../../lib/dom';
import { loadJiraIssue } from '../../redux/jira/actions';
import { selectJiraIssueOptional } from '../../redux/jira/selectors';
import { encodeHtml } from '../../utils';
import type { FieldType } from '../field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  isBrokenDatabase,
} from '../utils';

import { isNonEmptyJiraIssueValue } from './jiraIssue';

const parseRichText = (value: RichText): string => {
  try {
    if (value.type === 'adf') {
      const smartLinkNodeToUrl: (node: PMNode) => string = (node) => node.attrs.url ?? '';

      const schemaSmartLinkOverride = new Schema({
        nodes: defaultSchema.spec.nodes
          .update('inlineCard', {
            ...defaultSchema.spec.nodes.get('inlineCard'),
            leafText: smartLinkNodeToUrl,
          })
          .update('blockCard', {
            ...defaultSchema.spec.nodes.get('blockCard'),
            leafText: smartLinkNodeToUrl,
          })
          .update('embedCard', {
            ...defaultSchema.spec.nodes.get('embedCard'),
            leafText: smartLinkNodeToUrl,
          }),
        marks: defaultSchema.spec.marks,
      });

      const adf = JSON.parse(value.text);
      return PMNode.fromJSON(schemaSmartLinkOverride, {
        ...adf,
        content: [...adf.content.slice(0, 2)],
      }).textContent;
    }

    return '';
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (e) {
    if (e instanceof Error) {
      logError('Error parsing richtext for export', e);
    }

    return '';
  }
};

/**
 * Returns the Jira issue field in the same database that the Jira issue details field references.
 */
export function getReferencedJiraIssueDetailsField({
  database,
  field,
}: FieldWithContext<DatabaseFieldType, ObjectMode>):
  | DatabaseField<DatabaseFieldType.JIRA_ISSUE>
  | undefined {
  const referencedField = getDatabaseFieldOptional(database, field.settings.fieldId);
  if (referencedField?.type !== DatabaseFieldType.JIRA_ISSUE) {
    return undefined;
  } else {
    return referencedField as DatabaseField<DatabaseFieldType.JIRA_ISSUE>;
  }
}

export function getReferencedJiraIssueRefs<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
  state: State
): JiraIssueRef[] {
  const referencedField = getReferencedJiraIssueDetailsField({ database, field });
  const referencedValue =
    referencedField && getConvertedDatabaseValue(database, referencedField, entry, state);

  return (
    referencedValue?.value.flatMap((v) =>
      isNonEmptyJiraIssueValue(v)
        ? [
            {
              cloudId: referencedField!.settings.cloudId,
              serverId: referencedField!.settings.jiraServerId,
              key: v.key,
            },
          ]
        : []
    ) ?? []
  );
}

/**
 * Returns the Jira issues that the referenced Jira issue field references.
 */
export function getReferencedJiraIssues<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
  state: State
): ResolvedJiraIssueRef[] {
  return getReferencedJiraIssueRefs({ database, field, entry }, state).flatMap((ref) => {
    const resolved = selectJiraIssueOptional(state, ref);
    return resolved ? [resolved] : [];
  });
}

export function getReferencedJiraValues<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> &
    Optional<EntryWithContext<ObjectMode>, 'entry'>,
  state: State | undefined
): ResolvedJiraIssueRef['columns'][string][] {
  if (!entry || !state) {
    return [];
  }
  const issues = getReferencedJiraIssues({ database, field, entry }, state);
  const jiraField = field.settings.jiraField && field.settings.jiraField;

  return issues.map((issue) =>
    jiraField && issue.columns[jiraField]
      ? issue.columns[jiraField]
      : { html: '', text: '', value: '', type: 'string' }
  );
}

export function isJiraIssueDetailsResolved<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
  state: State
): boolean {
  const issueRefs = getReferencedJiraIssueRefs({ database, field, entry }, state);
  if (issueRefs.length === 0) {
    return true;
  }
  const resolvedIssues = getReferencedJiraIssues({ database, field, entry }, state);
  return resolvedIssues.length > 0;
}

const jiraIssueDetailsFieldType: FieldType<DatabaseFieldType.JIRA_ISSUE_DETAILS> = {
  supportedFilterTypes: [DatabaseFilterType.CONTAINS],

  isCsvImportSupported: false,

  renderExportView: ({ database, field, entry }, state) => {
    return getReferencedJiraValues({ database, field, entry }, state)
      .map((value) => {
        if (value.type === 'richtext') {
          const data = value.data as RichText | undefined;

          if (data) {
            return encodeHtml(parseRichText(data));
          }
        }

        // Convert the HTML to XHTML (for example the Priority field contains
        // unclosed img tags)
        return encodeHtml(parseStorageFormat(value.html).html());
      })
      .join('<br/>');
  },

  getInitialValue: () => undefined,

  isEmptyValue: ({ database, field, entry }, state) => {
    if (!entry || !state) {
      return true;
    }

    /*
     * If no issue refs exist (parent Jira field is empty), returns empty
     */
    const issueRefs = getReferencedJiraIssueRefs({ database, field, entry }, state);
    if (issueRefs.length === 0) {
      return true;
    }

    /*
     * If issue refs exist but haven't resolved yet, returns not empty to keep the field visible while loading
     */
    const resolvedIssues = getReferencedJiraIssues({ database, field, entry }, state);
    if (resolvedIssues.length === 0) {
      return false;
    }

    /*
     * If issues are resolved but the specific column (e.g., "updated") hasn't been fetched yet,
     * return false to keep the field visible while the column loads. This prevents fields like
     * "Updated" from incorrectly being hidden during the incremental fetching process.
     */
    const jiraField = field.settings.jiraField;
    const allColumnsLoaded = resolvedIssues.every(
      (issue) => jiraField && issue.columns[jiraField] !== undefined
    );
    if (!allColumnsLoaded) {
      return false;
    }

    const values = getReferencedJiraValues({ database, field, entry }, state);
    return values.every((value) => value.text.trim() === '');
  },

  isEqualValue: (context1, context2, state) => {
    const values1 = getReferencedJiraValues(context1, state);
    const values2 = getReferencedJiraValues(context2, state);
    return isEqual(values1, values2);
  },

  getStringRepresentation: ({ database, field, entry }, state) => {
    if (state) {
      return getReferencedJiraValues({ database, field, entry }, state)
        .map((value) => {
          if (value.type === 'richtext') {
            const data = value.data as RichText | undefined;

            if (data) {
              return parseRichText(data);
            }
          }

          return value.text;
        })
        .join('\n');
    } else {
      return '';
    }
  },

  loadDependencies: (
    { contentId, field, entryId },
    { dispatch, selector, selectDatabase }
  ) => {
    const jiraField = field.settings.jiraField;
    if (jiraField) {
      const issueRefs = selector((state) => {
        const _db = selectDatabase(state, { contentId });
        const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
        const entry =
          database && entryId ? getDatabaseEntryOptional(database, entryId) : undefined;
        return database && entry
          ? getReferencedJiraIssueRefs({ database, field, entry }, state)
          : [];
      });

      for (const ref of issueRefs) {
        dispatch(loadJiraIssue(ref, { columns: [jiraField] }));
      }
    }
  },

  noDefaultValue: true,
};

export default jiraIssueDetailsFieldType;
